import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  RefreshCw,
  Expand,
  Shrink,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlignLeft,
  Eye,
  Pencil,
  Feather,
  Zap,
  Music,
  Drama,
  Smile,
  BookOpen,
  Moon,
  Sunrise,
  FileText,
  Wind,
  Layers,
  SkipForward,
  ImagePlus,
  Send,
  MessageSquarePlus,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api, AiLimitError } from '../../lib/api';
import { notify } from '../../lib/toast';
import { IllustrationModal } from './illustration-modal';
import { useAIQualityStore, AI_MODEL_MAP } from '../../stores/ai-quality-store';

interface AIMenuProps {
  selectedText: string;
  selectionRange: { from: number; to: number };
  projectId: string;
  chapterId?: string;
  onApplyResult: (result: string, range: { from: number; to: number }) => void;
  /** Called with the data URL when an illustration should be inserted into the editor */
  onInsertImage: (src: string) => void;
  onClose: () => void;
}

type ActionId =
  | 'rewrite'
  | 'expand'
  | 'shorten'
  | 'continue'
  | 'grammar'
  | 'simplify'
  | 'show-not-tell'
  | 'describe'
  | 'custom';

type ToneId =
  | 'formal'
  | 'casual'
  | 'poetic'
  | 'dramatic'
  | 'humorous'
  | 'literary'
  | 'dark'
  | 'hopeful';

type StyleId = 'concise' | 'descriptive' | 'fast-paced' | 'lyrical';

type AmountId = 'slight' | 'moderate' | 'strong';

export function AIMenu({
  selectedText,
  selectionRange,
  projectId,
  chapterId,
  onApplyResult,
  onInsertImage,
  onClose,
}: AIMenuProps) {
  const { t } = useTranslation();
  const { quality: aiQuality } = useAIQualityStore();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [showTones, setShowTones] = useState(false);
  const [showStyles, setShowStyles] = useState(false);
  const [showExpandAmounts, setShowExpandAmounts] = useState(false);
  const [showShortenAmounts, setShowShortenAmounts] = useState(false);
  const [showIllustrationModal, setShowIllustrationModal] = useState(false);
  const [customPromptText, setCustomPromptText] = useState('');
  const customInputRef = useRef<HTMLTextAreaElement>(null);

  const closeAllSubmenus = () => {
    setShowTones(false);
    setShowStyles(false);
    setShowExpandAmounts(false);
    setShowShortenAmounts(false);
  };

  const runAssist = async (
    action: ActionId | 'tone' | 'style',
    options?: {
      tone?: string;
      style?: string;
      amount?: AmountId;
      customPrompt?: string;
    },
    key?: string,
  ) => {
    if (isLoading) return;

    const resolvedKey = key ?? action;
    setIsLoading(true);
    setLoadingKey(resolvedKey);

    try {
      const result = await api.aiAssist({
        projectId,
        chapterId,
        selection: selectedText,
        action,
        options: options?.tone || options?.style
          ? { tone: options.tone, style: options.style }
          : undefined,
        amount: options?.amount,
        customPrompt: options?.customPrompt,
        model: AI_MODEL_MAP[aiQuality],
      });
      onApplyResult(result, selectionRange);
      notify.success(t('ai.operationComplete', 'Texto processado com sucesso'));
    } catch (error: unknown) {
      if (error instanceof AiLimitError) {
        notify.aiLimitReached(error.type);
      } else {
        notify.error(t('ai.operationFailed', 'Falha ao processar o texto'));
      }
    } finally {
      setIsLoading(false);
      setLoadingKey(null);
      onClose();
    }
  };

  const handleCustomPrompt = () => {
    const prompt = customPromptText.trim();
    if (!prompt || isLoading) return;
    runAssist('custom', { customPrompt: prompt }, 'custom');
  };

  const amountLevels: Array<{ id: AmountId; label: string }> = [
    { id: 'slight', label: t('ai.amountSlight', 'Pouco') },
    { id: 'moderate', label: t('ai.amountModerate', 'Médio') },
    { id: 'strong', label: t('ai.amountStrong', 'Bastante') },
  ];

  const simpleTransformActions: Array<{
    id: ActionId;
    icon: React.ElementType;
    label: string;
    description: string;
  }> = [
    {
      id: 'rewrite',
      icon: RefreshCw,
      label: t('ai.rewrite', 'Reescrever'),
      description: t('ai.rewriteDesc', 'Reescreve de forma diferente'),
    },
    {
      id: 'continue',
      icon: SkipForward,
      label: t('ai.continue', 'Continuar'),
      description: t('ai.continueDesc', 'Continua a partir deste trecho'),
    },
  ];

  const grammarActions: Array<{
    id: ActionId;
    icon: React.ElementType;
    label: string;
    description: string;
  }> = [
    {
      id: 'grammar',
      icon: CheckCircle2,
      label: t('ai.grammar', 'Corrigir gramática'),
      description: t('ai.grammarDesc', 'Corrige erros gramaticais'),
    },
    {
      id: 'simplify',
      icon: AlignLeft,
      label: t('ai.simplify', 'Simplificar'),
      description: t('ai.simplifyDesc', 'Linguagem mais clara e direta'),
    },
    {
      id: 'show-not-tell',
      icon: Eye,
      label: t('ai.showNotTell', 'Mostre, não conte'),
      description: t('ai.showNotTellDesc', 'Converte relato em cena'),
    },
    {
      id: 'describe',
      icon: Pencil,
      label: t('ai.describe', 'Enriquecer descrição'),
      description: t('ai.describeDesc', 'Adiciona detalhes sensoriais'),
    },
  ];

  const tones: Array<{ id: ToneId; icon: React.ElementType; label: string }> = [
    { id: 'formal', icon: FileText, label: t('ai.toneForma', 'Formal') },
    { id: 'casual', icon: Smile, label: t('ai.toneCasual', 'Casual') },
    { id: 'poetic', icon: Feather, label: t('ai.tonePoetic', 'Poético') },
    { id: 'dramatic', icon: Drama, label: t('ai.toneDramatic', 'Dramático') },
    { id: 'humorous', icon: Zap, label: t('ai.toneHumorous', 'Bem-humorado') },
    { id: 'literary', icon: BookOpen, label: t('ai.toneLiterary', 'Literário') },
    { id: 'dark', icon: Moon, label: t('ai.toneDark', 'Sombrio') },
    { id: 'hopeful', icon: Sunrise, label: t('ai.toneHopeful', 'Esperançoso') },
  ];

  const styles: Array<{ id: StyleId; icon: React.ElementType; label: string }> = [
    { id: 'concise', icon: Layers, label: t('ai.styleConcise', 'Conciso') },
    { id: 'descriptive', icon: Music, label: t('ai.styleDescriptive', 'Descritivo') },
    { id: 'fast-paced', icon: Wind, label: t('ai.styleFastPaced', 'Ritmo acelerado') },
    { id: 'lyrical', icon: Feather, label: t('ai.styleLyrical', 'Lírico') },
  ];

  return (
    <div className="flex max-h-[inherit] w-56 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5">
        <div className="p-1 rounded-md bg-primary/10">
          <Sparkles size={12} className="text-primary" />
        </div>
        <span className="text-xs font-semibold text-foreground">
          {t('ai.ghostwriter', 'IA Ghostwriter')}
        </span>
      </div>

      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-1.5">
        {/* --- INSTRUÇÃO LIVRE (no topo) --- */}
        <GroupLabel label={t('ai.groupCustom', 'Instrução livre')} />
        <div className="px-2.5 py-1.5">
          <div className="flex items-start gap-1.5">
            <MessageSquarePlus
              size={13}
              className="text-muted-foreground flex-shrink-0 mt-2"
            />
            <div className="flex-1 flex flex-col gap-1.5">
              <textarea
                ref={customInputRef}
                value={customPromptText}
                onChange={(e) => setCustomPromptText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCustomPrompt();
                  }
                }}
                placeholder={t(
                  'ai.customPromptPlaceholder',
                  'Peça à IA com suas palavras…',
                )}
                rows={2}
                disabled={isLoading}
                className={cn(
                  'w-full resize-none rounded-lg border border-border bg-muted/50',
                  'px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60',
                  'focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary',
                  'disabled:opacity-50 transition-colors',
                )}
              />
              <button
                onClick={handleCustomPrompt}
                disabled={isLoading || !customPromptText.trim()}
                className={cn(
                  'w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium',
                  'bg-primary text-primary-foreground transition-colors',
                  'hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed',
                  isLoading && loadingKey === 'custom' && 'opacity-80',
                )}
              >
                {isLoading && loadingKey === 'custom' ? (
                  <>
                    <Loader2 size={11} className="animate-spin" />
                    {t('ai.customProcessing', 'Processando...')}
                  </>
                ) : (
                  <>
                    <Send size={11} />
                    {t('ai.customSend', 'Enviar')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <Divider />

        {/* --- TRANSFORMAR --- */}
        <GroupLabel label={t('ai.groupTransform', 'Transformar')} />

        {/* Simple actions: rewrite, continue */}
        {simpleTransformActions.map((action) => (
          <ActionItem
            key={action.id}
            icon={action.icon}
            label={action.label}
            description={action.description}
            loading={isLoading && loadingKey === action.id}
            disabled={isLoading}
            onClick={() => runAssist(action.id)}
          />
        ))}

        {/* Expand with amount submenu */}
        <div>
          <button
            onClick={() => {
              setShowExpandAmounts((v) => !v);
              setShowShortenAmounts(false);
              setShowTones(false);
              setShowStyles(false);
            }}
            disabled={isLoading}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors',
              'hover:bg-muted text-foreground disabled:opacity-50',
              showExpandAmounts && 'bg-muted',
            )}
          >
            <Expand size={14} className="text-muted-foreground flex-shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium leading-none">
                {t('ai.expand', 'Expandir')}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('ai.expandDesc', 'Adiciona mais detalhe')}
              </div>
            </div>
            <ChevronRight
              size={13}
              className={cn(
                'text-muted-foreground flex-shrink-0 transition-transform',
                showExpandAmounts && 'rotate-90',
              )}
            />
          </button>

          {showExpandAmounts && (
            <div className="mt-0.5 ml-4 flex gap-1 px-1 py-1">
              {amountLevels.map(({ id, label }) => (
                <button
                  key={id}
                  disabled={isLoading}
                  onClick={() => runAssist('expand', { amount: id }, `expand-${id}`)}
                  className={cn(
                    'flex-1 py-1.5 rounded-md text-xs font-medium transition-colors',
                    'border border-border hover:bg-primary/10 hover:border-primary/40',
                    'text-muted-foreground hover:text-foreground disabled:opacity-50',
                    isLoading && loadingKey === `expand-${id}` &&
                      'bg-primary/10 border-primary/50 text-primary',
                  )}
                >
                  {isLoading && loadingKey === `expand-${id}` ? (
                    <Loader2 size={10} className="animate-spin mx-auto" />
                  ) : (
                    label
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Shorten with amount submenu */}
        <div>
          <button
            onClick={() => {
              setShowShortenAmounts((v) => !v);
              setShowExpandAmounts(false);
              setShowTones(false);
              setShowStyles(false);
            }}
            disabled={isLoading}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors',
              'hover:bg-muted text-foreground disabled:opacity-50',
              showShortenAmounts && 'bg-muted',
            )}
          >
            <Shrink size={14} className="text-muted-foreground flex-shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium leading-none">
                {t('ai.shorten', 'Encurtar')}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('ai.shortenDesc', 'Torna mais conciso')}
              </div>
            </div>
            <ChevronRight
              size={13}
              className={cn(
                'text-muted-foreground flex-shrink-0 transition-transform',
                showShortenAmounts && 'rotate-90',
              )}
            />
          </button>

          {showShortenAmounts && (
            <div className="mt-0.5 ml-4 flex gap-1 px-1 py-1">
              {amountLevels.map(({ id, label }) => (
                <button
                  key={id}
                  disabled={isLoading}
                  onClick={() => runAssist('shorten', { amount: id }, `shorten-${id}`)}
                  className={cn(
                    'flex-1 py-1.5 rounded-md text-xs font-medium transition-colors',
                    'border border-border hover:bg-primary/10 hover:border-primary/40',
                    'text-muted-foreground hover:text-foreground disabled:opacity-50',
                    isLoading && loadingKey === `shorten-${id}` &&
                      'bg-primary/10 border-primary/50 text-primary',
                  )}
                >
                  {isLoading && loadingKey === `shorten-${id}` ? (
                    <Loader2 size={10} className="animate-spin mx-auto" />
                  ) : (
                    label
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* --- TOM --- */}
        <GroupLabel label={t('ai.groupTone', 'Tom')} />
        <div>
          <button
            onClick={() => {
              setShowTones((v) => !v);
              setShowStyles(false);
              setShowExpandAmounts(false);
              setShowShortenAmounts(false);
            }}
            disabled={isLoading}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors',
              'hover:bg-muted text-foreground disabled:opacity-50',
              showTones && 'bg-muted',
            )}
          >
            <Music size={14} className="text-muted-foreground flex-shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium leading-none">
                {t('ai.adjustTone', 'Mudar tom')}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('ai.adjustToneDesc', 'Escolha um tom para o texto')}
              </div>
            </div>
            <ChevronRight
              size={13}
              className={cn(
                'text-muted-foreground flex-shrink-0 transition-transform',
                showTones && 'rotate-90',
              )}
            />
          </button>

          {showTones && (
            <div className="mt-0.5 ml-4 space-y-0.5">
              {tones.map((tone) => (
                <SubItem
                  key={tone.id}
                  icon={tone.icon}
                  label={tone.label}
                  loading={isLoading && loadingKey === `tone-${tone.id}`}
                  disabled={isLoading}
                  onClick={() =>
                    runAssist('tone', { tone: tone.id }, `tone-${tone.id}`)
                  }
                />
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* --- ESTILO --- */}
        <GroupLabel label={t('ai.groupStyle', 'Estilo')} />
        <div>
          <button
            onClick={() => {
              setShowStyles((v) => !v);
              setShowTones(false);
              setShowExpandAmounts(false);
              setShowShortenAmounts(false);
            }}
            disabled={isLoading}
            className={cn(
              'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors',
              'hover:bg-muted text-foreground disabled:opacity-50',
              showStyles && 'bg-muted',
            )}
          >
            <Layers size={14} className="text-muted-foreground flex-shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium leading-none">
                {t('ai.applyStyle', 'Aplicar estilo')}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('ai.applyStyleDesc', 'Muda a voz e o ritmo do texto')}
              </div>
            </div>
            <ChevronRight
              size={13}
              className={cn(
                'text-muted-foreground flex-shrink-0 transition-transform',
                showStyles && 'rotate-90',
              )}
            />
          </button>

          {showStyles && (
            <div className="mt-0.5 ml-4 space-y-0.5">
              {styles.map((style) => (
                <SubItem
                  key={style.id}
                  icon={style.icon}
                  label={style.label}
                  loading={isLoading && loadingKey === `style-${style.id}`}
                  disabled={isLoading}
                  onClick={() =>
                    runAssist('style', { style: style.id }, `style-${style.id}`)
                  }
                />
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* --- GRAMATICA --- */}
        <GroupLabel label={t('ai.groupGrammar', 'Gramática e clareza')} />
        {grammarActions.map((action) => (
          <ActionItem
            key={action.id}
            icon={action.icon}
            label={action.label}
            description={action.description}
            loading={isLoading && loadingKey === action.id}
            disabled={isLoading}
            onClick={() => runAssist(action.id)}
          />
        ))}

        <Divider />

        {/* --- ILUSTRAR --- */}
        <GroupLabel label={t('ai.groupIllustrate', 'Ilustração')} />
        <ActionItem
          icon={ImagePlus}
          label={t('ai.illustrateScene', 'Ilustrar cena')}
          description={t('ai.illustrateSceneDesc', 'Gera uma imagem do trecho selecionado')}
          loading={false}
          disabled={isLoading}
          onClick={() => {
            closeAllSubmenus();
            setShowIllustrationModal(true);
          }}
        />
      </div>

      {/* Illustration modal — rendered outside the scroll container */}
      {showIllustrationModal && (
        <IllustrationModal
          selection={selectedText}
          projectId={projectId}
          chapterId={chapterId}
          open={showIllustrationModal}
          onOpenChange={(open) => {
            setShowIllustrationModal(open);
            if (!open) onClose();
          }}
          onInsert={(imageUrl) => {
            onInsertImage(imageUrl);
          }}
        />
      )}
    </div>
  );
}

// ---- Sub-components ----

function GroupLabel({ label }: { label: string }) {
  return (
    <div className="px-2.5 pt-1.5 pb-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-1 h-px bg-border/60" />;
}

function ActionItem({
  icon: Icon,
  label,
  description,
  loading,
  disabled,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors',
        'hover:bg-muted text-foreground disabled:opacity-50',
        loading && 'bg-primary/10',
      )}
    >
      {loading ? (
        <Loader2 size={14} className="text-primary animate-spin flex-shrink-0" />
      ) : (
        <Icon size={14} className="text-muted-foreground flex-shrink-0" />
      )}
      <div className="flex-1 text-left">
        <div className="text-sm font-medium leading-none">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
      </div>
    </button>
  );
}

function SubItem({
  icon: Icon,
  label,
  loading,
  disabled,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors',
        'hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50',
        loading && 'bg-primary/10 text-primary',
      )}
    >
      {loading ? (
        <Loader2 size={12} className="animate-spin flex-shrink-0" />
      ) : (
        <Icon size={12} className="flex-shrink-0" />
      )}
      <span>{label}</span>
    </button>
  );
}
